'use client';

/**
 * Team Topology Designer
 * Visual tool for designing team structures based on Team Topologies book
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import {
  TeamTopologyDiagram,
  Team,
  TeamInteraction,
  TeamType,
  InteractionMode,
  CanvasSettings,
  TopologyFeatures,
  TEAM_TYPE_CONFIG,
  DEFAULT_CANVAS_SETTINGS,
  DEFAULT_TEAM_SIZE,
} from '@/lib/team-topology/types';
import {
  getAllDiagrams,
  saveDiagram,
  deleteDiagram,
  createDiagram,
  getDiagram,
} from '@/lib/team-topology/storage';
import {
  snapToGrid,
  findNonOverlappingPosition,
  getTeamColor,
} from '@/lib/team-topology/canvas-utils';
import { useAuth } from '@/lib/auth/AuthContext';

// Dynamic imports for components
const TeamNode = dynamic(
  () => import('@/components/team-topology/TeamNode'),
  { ssr: false }
);

const InteractionLine = dynamic(
  () => import('@/components/team-topology/InteractionLine'),
  { ssr: false }
);

const TeamPalette = dynamic(
  () => import('@/components/team-topology/TeamPalette'),
  { ssr: false }
);

const ConwayChecker = dynamic(
  () => import('@/components/team-topology/ConwayChecker'),
  { ssr: false }
);

// Premium tier hierarchy
const TIER_HIERARCHY = { free: 0, pro: 1, team: 2 };

function useUserTier(): 'free' | 'pro' | 'team' {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? 'pro' : 'free';
}

function useFeatures(tier: 'free' | 'pro' | 'team'): TopologyFeatures {
  const tierLevel = TIER_HIERARCHY[tier];
  return {
    canEdit: tierLevel >= TIER_HIERARCHY.pro,
    canSave: tierLevel >= TIER_HIERARCHY.pro,
    maxTeams: tier === 'free' ? 3 : 999,
    canExportPng: tierLevel >= TIER_HIERARCHY.pro,
    canExportSvg: tierLevel >= TIER_HIERARCHY.team,
    canUseEvolutionTimeline: tierLevel >= TIER_HIERARCHY.pro,
    canUseConwayChecker: tierLevel >= TIER_HIERARCHY.pro,
  };
}

type SidePanel = 'palette' | 'conway' | 'none';

export default function TeamTopologyPage() {
  const { isAuthenticated, login } = useAuth();
  const userTier = useUserTier();
  const features = useFeatures(userTier);

  // Refs
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [diagram, setDiagram] = useState<TeamTopologyDiagram | null>(null);
  const [savedDiagrams, setSavedDiagrams] = useState<TeamTopologyDiagram[]>([]);
  const [settings, setSettings] = useState<CanvasSettings>(DEFAULT_CANVAS_SETTINGS);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Selection state
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedInteractionId, setSelectedInteractionId] = useState<string | null>(null);

  // Connection mode state
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionMode, setConnectionMode] = useState<InteractionMode | null>(null);
  const [connectionSourceId, setConnectionSourceId] = useState<string | null>(null);

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // UI state
  const [sidePanel, setSidePanel] = useState<SidePanel>('palette');
  const [showDiagramPicker, setShowDiagramPicker] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);

  // Load diagrams on mount
  useEffect(() => {
    async function loadData() {
      try {
        const diagrams = await getAllDiagrams();
        setSavedDiagrams(diagrams);

        if (diagrams.length > 0) {
          setDiagram(diagrams[0]);
        }
      } catch (error) {
        console.error('Failed to load diagrams:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, []);

  // Current snapshot helpers
  const currentSnapshot = diagram?.currentState;
  // Memoize to avoid new array references on every render
  const teams = useMemo(() => currentSnapshot?.teams || [], [currentSnapshot?.teams]);
  const interactions = useMemo(() => currentSnapshot?.interactions || [], [currentSnapshot?.interactions]);

  // Update diagram helper
  const updateDiagram = useCallback((
    updater: (d: TeamTopologyDiagram) => TeamTopologyDiagram
  ) => {
    if (!diagram) return;
    setDiagram(updater(diagram));
    setHasUnsavedChanges(true);
  }, [diagram]);

  // Save diagram
  const handleSave = useCallback(async () => {
    if (!diagram || !features.canSave) return;

    try {
      await saveDiagram(diagram);
      setSavedDiagrams(prev =>
        prev.map(d => d.id === diagram.id ? diagram : d)
      );
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save diagram:', error);
    }
  }, [diagram, features.canSave]);

  // Create new diagram
  const handleNewDiagram = useCallback(async () => {
    try {
      const newDiagram = await createDiagram('New Topology');
      setDiagram(newDiagram);
      setSavedDiagrams(prev => [newDiagram, ...prev]);
      setShowDiagramPicker(false);
    } catch (error) {
      console.error('Failed to create diagram:', error);
    }
  }, []);

  // Add team
  const handleAddTeam = useCallback((type: TeamType) => {
    if (!diagram || !features.canEdit) return;
    if (teams.length >= features.maxTeams) {
      alert(`Free tier is limited to ${features.maxTeams} teams. Upgrade to add more.`);
      return;
    }

    const position = findNonOverlappingPosition(
      teams,
      { x: 100 + (teams.length % 3) * 200, y: 100 + Math.floor(teams.length / 3) * 150 },
      settings
    );

    const newTeam: Team = {
      id: `team-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: `${TEAM_TYPE_CONFIG[type].label} Team`,
      type,
      position,
    };

    updateDiagram(d => ({
      ...d,
      currentState: {
        ...d.currentState,
        teams: [...d.currentState.teams, newTeam],
      },
    }));

    setSelectedTeamId(newTeam.id);
    setSelectedInteractionId(null);
  }, [diagram, features, teams, settings, updateDiagram]);

  // Delete team
  const handleDeleteTeam = useCallback((teamId: string) => {
    if (!features.canEdit || !confirm('Delete this team and all its connections?')) return;

    updateDiagram(d => ({
      ...d,
      currentState: {
        ...d.currentState,
        teams: d.currentState.teams.filter(t => t.id !== teamId),
        interactions: d.currentState.interactions.filter(
          i => i.sourceTeamId !== teamId && i.targetTeamId !== teamId
        ),
      },
    }));

    if (selectedTeamId === teamId) setSelectedTeamId(null);
  }, [features.canEdit, selectedTeamId, updateDiagram]);

  // Start connection
  const handleStartConnection = useCallback((mode: InteractionMode) => {
    if (isConnecting && connectionMode === mode) {
      // Cancel if clicking the same mode
      setIsConnecting(false);
      setConnectionMode(null);
      setConnectionSourceId(null);
    } else {
      setIsConnecting(true);
      setConnectionMode(mode);
      setConnectionSourceId(null);
    }
  }, [isConnecting, connectionMode]);

  // Handle team click (for connections)
  const handleTeamClick = useCallback((teamId: string) => {
    if (isConnecting && connectionMode) {
      if (!connectionSourceId) {
        setConnectionSourceId(teamId);
      } else if (connectionSourceId !== teamId) {
        // Create connection
        const newInteraction: TeamInteraction = {
          id: `interaction-${Date.now()}`,
          sourceTeamId: connectionSourceId,
          targetTeamId: teamId,
          mode: connectionMode,
          temporary: connectionMode !== 'x-as-a-service',
        };

        updateDiagram(d => ({
          ...d,
          currentState: {
            ...d.currentState,
            interactions: [...d.currentState.interactions, newInteraction],
          },
        }));

        // Reset connection state
        setIsConnecting(false);
        setConnectionMode(null);
        setConnectionSourceId(null);
        setSelectedInteractionId(newInteraction.id);
        setSelectedTeamId(null);
      }
    } else {
      setSelectedTeamId(teamId);
      setSelectedInteractionId(null);
    }
  }, [isConnecting, connectionMode, connectionSourceId, updateDiagram]);

  // Delete interaction
  const handleDeleteInteraction = useCallback((interactionId: string) => {
    if (!features.canEdit || !confirm('Delete this connection?')) return;

    updateDiagram(d => ({
      ...d,
      currentState: {
        ...d.currentState,
        interactions: d.currentState.interactions.filter(i => i.id !== interactionId),
      },
    }));

    if (selectedInteractionId === interactionId) setSelectedInteractionId(null);
  }, [features.canEdit, selectedInteractionId, updateDiagram]);

  // Drag handlers
  const handleDragStart = useCallback((teamId: string, e: React.MouseEvent | React.TouchEvent) => {
    if (!features.canEdit) return;

    const team = teams.find(t => t.id === teamId);
    if (!team) return;

    e.preventDefault();
    e.stopPropagation();

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const x = (clientX - rect.left) / settings.zoom - settings.panX;
    const y = (clientY - rect.top) / settings.zoom - settings.panY;

    setIsDragging(true);
    setSelectedTeamId(teamId);
    setDragOffset({ x: x - team.position.x, y: y - team.position.y });
  }, [features.canEdit, teams, settings]);

  const handleDragMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || !selectedTeamId) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const x = (clientX - rect.left) / settings.zoom - settings.panX - dragOffset.x;
    const y = (clientY - rect.top) / settings.zoom - settings.panY - dragOffset.y;

    const position = settings.snapToGrid
      ? snapToGrid({ x, y }, settings.gridSize)
      : { x, y };

    updateDiagram(d => ({
      ...d,
      currentState: {
        ...d.currentState,
        teams: d.currentState.teams.map(t =>
          t.id === selectedTeamId ? { ...t, position } : t
        ),
      },
    }));
  }, [isDragging, selectedTeamId, settings, dragOffset, updateDiagram]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Export to PNG
  const handleExportPng = useCallback(() => {
    if (!svgRef.current || !features.canExportPng) return;

    const svg = svgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      ctx.scale(2, 2);
      ctx.fillStyle = '#0A1E2E';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const link = document.createElement('a');
      link.download = `${diagram?.name || 'topology'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(data)));
  }, [diagram?.name, features.canExportPng]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground-secondary">Loading...</div>
      </div>
    );
  }

  // No diagram - show create prompt
  if (!diagram) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-2xl mx-auto">
          <Link href="/tools" className="text-foreground-tertiary hover:text-foreground text-sm mb-4 inline-flex items-center gap-1">
            ← Back to Tools
          </Link>
          <h1 className="text-3xl font-bold text-foreground mt-2">Team Topology Designer</h1>
          <p className="text-foreground-secondary mt-2">
            Design team structures based on the Team Topologies book.
          </p>

          <div className="mt-8 p-6 bg-purple-500/10 border border-purple-500/30 rounded-lg">
            <div className="font-semibold text-purple-400">Premium Feature</div>
            <p className="text-sm text-foreground-secondary mt-1">
              Full editing, saving, and export require a Pro subscription.
            </p>
          </div>

          <button
            onClick={handleNewDiagram}
            className="mt-6 px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover"
          >
            Create New Topology
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-border bg-background/95 backdrop-blur px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <Link href="/tools" className="text-foreground-tertiary hover:text-foreground">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Link>
          <div>
            <h1 className="text-lg font-bold text-foreground">{diagram.name}</h1>
            <p className="text-xs text-foreground-tertiary">Team Topology Designer</p>
          </div>
          {hasUnsavedChanges && (
            <span className="text-xs px-2 py-1 bg-accent-muted text-accent rounded">
              Unsaved
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDiagramPicker(true)}
            className="p-2 text-foreground-tertiary hover:text-foreground"
            title="Open diagram"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </button>

          {features.canExportPng && (
            <button
              onClick={handleExportPng}
              className="p-2 text-foreground-tertiary hover:text-foreground"
              title="Export PNG"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
          )}

          {features.canSave ? (
            <button
              onClick={handleSave}
              disabled={!hasUnsavedChanges}
              className={`px-4 py-2 rounded font-semibold ${
                hasUnsavedChanges
                  ? 'bg-accent text-accent-foreground hover:bg-accent-hover'
                  : 'bg-cream/10 text-foreground-tertiary cursor-not-allowed'
              }`}
            >
              Save
            </button>
          ) : (
            <button
              onClick={() => login()}
              className="px-4 py-2 bg-purple-500 text-white rounded font-semibold hover:bg-purple-600"
            >
              Upgrade
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas */}
        <div
          ref={containerRef}
          className="flex-1 relative overflow-hidden"
          onMouseMove={isDragging ? handleDragMove : undefined}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchMove={isDragging ? handleDragMove : undefined}
          onTouchEnd={handleDragEnd}
        >
          <svg
            ref={svgRef}
            className="w-full h-full"
            style={{ background: '#0A1E2E' }}
            onClick={() => {
              if (!isDragging) {
                setSelectedTeamId(null);
                setSelectedInteractionId(null);
              }
            }}
          >
            {/* Grid */}
            {settings.gridEnabled && (
              <defs>
                <pattern
                  id="grid"
                  width={settings.gridSize}
                  height={settings.gridSize}
                  patternUnits="userSpaceOnUse"
                >
                  <circle cx={1} cy={1} r={1} fill="#E8D5B710" />
                </pattern>
              </defs>
            )}
            {settings.gridEnabled && (
              <rect width="100%" height="100%" fill="url(#grid)" />
            )}

            {/* Interaction lines */}
            <g>
              {interactions.map(interaction => {
                const sourceTeam = teams.find(t => t.id === interaction.sourceTeamId);
                const targetTeam = teams.find(t => t.id === interaction.targetTeamId);
                if (!sourceTeam || !targetTeam) return null;

                return (
                  <InteractionLine
                    key={interaction.id}
                    interaction={interaction}
                    sourceTeam={sourceTeam}
                    targetTeam={targetTeam}
                    isSelected={selectedInteractionId === interaction.id}
                    showLabel={settings.showInteractionLabels}
                    onSelect={() => {
                      setSelectedInteractionId(interaction.id);
                      setSelectedTeamId(null);
                    }}
                    onDelete={() => handleDeleteInteraction(interaction.id)}
                    canEdit={features.canEdit}
                  />
                );
              })}
            </g>

            {/* Teams */}
            <g>
              {teams.map(team => (
                <TeamNode
                  key={team.id}
                  team={team}
                  isSelected={selectedTeamId === team.id || connectionSourceId === team.id}
                  isDragging={isDragging && selectedTeamId === team.id}
                  onSelect={() => handleTeamClick(team.id)}
                  onDragStart={(e) => handleDragStart(team.id, e)}
                  onEdit={() => setEditingTeam(team)}
                  onDelete={() => handleDeleteTeam(team.id)}
                  canEdit={features.canEdit}
                />
              ))}
            </g>

            {/* Connection hint */}
            {isConnecting && connectionSourceId && (
              <text
                x="50%"
                y={30}
                textAnchor="middle"
                fill="#E89841"
                fontSize={14}
              >
                Click a target team to connect
              </text>
            )}
          </svg>

          {/* Zoom controls */}
          <div className="absolute bottom-4 left-4 flex gap-1 bg-background/80 backdrop-blur rounded border border-border p-1">
            <button
              onClick={() => setSettings(s => ({ ...s, zoom: Math.min(2, s.zoom + 0.1) }))}
              className="p-2 text-foreground-secondary hover:text-foreground"
            >
              +
            </button>
            <span className="px-2 py-2 text-sm text-foreground-tertiary">
              {Math.round(settings.zoom * 100)}%
            </span>
            <button
              onClick={() => setSettings(s => ({ ...s, zoom: Math.max(0.5, s.zoom - 0.1) }))}
              className="p-2 text-foreground-secondary hover:text-foreground"
            >
              -
            </button>
          </div>
        </div>

        {/* Side panel */}
        <div className="w-72 border-l border-border bg-background/50 overflow-y-auto shrink-0">
          {/* Panel tabs */}
          <div className="flex border-b border-border">
            <button
              onClick={() => setSidePanel('palette')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                sidePanel === 'palette' ? 'text-accent border-b-2 border-accent' : 'text-foreground-tertiary'
              }`}
            >
              Palette
            </button>
            <button
              onClick={() => setSidePanel('conway')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                sidePanel === 'conway' ? 'text-accent border-b-2 border-accent' : 'text-foreground-tertiary'
              }`}
            >
              Conway Check
            </button>
          </div>

          {/* Panel content */}
          <div className="p-4">
            {sidePanel === 'palette' && (
              <TeamPalette
                onAddTeam={handleAddTeam}
                onStartConnection={handleStartConnection}
                isConnecting={isConnecting}
                connectionMode={connectionMode}
                canEdit={features.canEdit}
              />
            )}
            {sidePanel === 'conway' && currentSnapshot && (
              features.canUseConwayChecker ? (
                <ConwayChecker
                  snapshot={currentSnapshot}
                  onTeamSelect={setSelectedTeamId}
                  onInteractionSelect={setSelectedInteractionId}
                />
              ) : (
                <div className="text-center p-4">
                  <p className="text-foreground-secondary mb-4">
                    Conway&apos;s Law checker requires a Pro subscription.
                  </p>
                  <button
                    onClick={() => login()}
                    className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
                  >
                    Upgrade to Pro
                  </button>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Diagram picker modal */}
      {showDiagramPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-border rounded-lg p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-foreground">Open Diagram</h2>
              <button
                onClick={() => setShowDiagramPicker(false)}
                className="p-1 text-foreground-tertiary hover:text-foreground"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {savedDiagrams.map(d => (
                <button
                  key={d.id}
                  onClick={async () => {
                    const loaded = await getDiagram(d.id);
                    if (loaded) setDiagram(loaded);
                    setShowDiagramPicker(false);
                  }}
                  className={`w-full text-left p-3 rounded border ${
                    d.id === diagram?.id
                      ? 'border-accent bg-accent-muted'
                      : 'border-border hover:border-border/40'
                  }`}
                >
                  <div className="font-semibold text-foreground">{d.name}</div>
                  <div className="text-xs text-foreground-tertiary">
                    {d.currentState.teams.length} teams • {d.currentState.interactions.length} connections
                  </div>
                </button>
              ))}
            </div>

            <button
              onClick={handleNewDiagram}
              className="w-full mt-4 p-3 border border-dashed border-border rounded text-foreground-secondary hover:border-border-muted0 hover:text-foreground"
            >
              + Create New Diagram
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
